import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Plus, AlertCircle, Trash2, Edit2, ShoppingCart, Tag, Search } from 'lucide-react';

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
}

interface Discount {
  id: number;
  name: string;
  code: string | null;
  type: 'PERCENTAGE' | 'FIXED';
  value: string;
  maxAmount: string | null;
  isActive: boolean;
}

interface SatItem {
  id: number;
  clave: string;
  descripcion?: string;
  nombre?: string;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  description?: string;
  price: string;
  taxRate: string;
  type: string;
  incomeAccountId: number;
  assetAccountId?: number;
  allowedPaymentMethods?: string;
  isActive: boolean;
  defaultDiscountId?: number | null;
  claveProdServ?: string | null;
  claveUnidad?: string | null;
  defaultDiscount?: Discount | null;
  incomeAccount?: Account;
  assetAccount?: Account;
}

const ALL_PAYMENT_METHODS = [
  { value: 'TRANSFER', label: 'Transferencia' },
  { value: 'CASH', label: 'Efectivo' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'CARD', label: 'Tarjeta' },
  { value: 'OTHER', label: 'Otro' },
];

function SatCombobox({
  label,
  value,
  onChange,
  items,
  loading,
  placeholder,
  getLabel,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  items: SatItem[];
  loading: boolean;
  placeholder: string;
  getLabel: (item: SatItem) => string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = items.filter(item => {
    const q = search.toLowerCase();
    const lbl = getLabel(item).toLowerCase();
    return lbl.includes(q) || item.clave.toLowerCase().includes(q);
  }).slice(0, 50);

  const selectedItem = items.find(i => i.clave === value);
  const displayValue = selectedItem ? getLabel(selectedItem) : '';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        onClick={() => { setOpen(true); setSearch(''); }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer bg-white flex items-center justify-between min-h-[38px]"
      >
        <span className={displayValue ? 'text-gray-900 text-sm' : 'text-gray-400 text-sm'}>
          {displayValue || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <Search className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por clave o descripción..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-sm text-gray-500">Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-center text-sm text-gray-500">
                {items.length === 0 ? 'No hay datos. Carga el catálogo SAT en Facturación.' : 'Sin resultados'}
              </div>
            ) : (
              filtered.map(item => (
                <div
                  key={item.id}
                  onClick={() => { onChange(item.clave); setOpen(false); setSearch(''); }}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${value === item.clave ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-800'}`}
                >
                  <span className="font-mono text-xs text-blue-700 mr-2">{item.clave}</span>
                  {item.descripcion || item.nombre}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Productos() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [satProdServ, setSatProdServ] = useState<SatItem[]>([]);
  const [satUnidades, setSatUnidades] = useState<SatItem[]>([]);
  const [satLoading, setSatLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    price: '',
    taxRate: '0',
    type: 'PRODUCT',
    incomeAccountId: '',
    assetAccountId: '',
    allowedPaymentMethods: ALL_PAYMENT_METHODS.map(m => m.value),
    isActive: true,
    defaultDiscountId: '' as string,
    claveProdServ: '',
    claveUnidad: '',
  });

  useEffect(() => {
    loadProducts();
    loadAccounts();
    loadDiscounts();
    loadSatCatalogs();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/api/sales/products', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al cargar productos');
      const data = await response.json();
      const sorted = Array.isArray(data) ? data.sort((a: Product, b: Product) => a.sku.localeCompare(b.sku, 'es')) : [];
      setProducts(sorted);
    } catch (err) {
      console.error('Error cargando productos:', err);
      setError('Error al cargar los productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSatCatalogs = async () => {
    setSatLoading(true);
    try {
      const [ps, un] = await Promise.all([
        fetch('/api/cfdi/sat/prod-serv', { credentials: 'include' }),
        fetch('/api/cfdi/sat/unidades', { credentials: 'include' }),
      ]);
      if (ps.ok) setSatProdServ(await ps.json());
      if (un.ok) setSatUnidades(await un.json());
    } catch {
      /* silently ignore if no CFDI permission */
    } finally {
      setSatLoading(false);
    }
  };

  const loadDiscounts = async () => {
    try {
      const response = await fetch('/api/discounts?active=true', { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setDiscounts(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const loadAccounts = async () => {
    try {
      const response = await fetch('/api/finance/accounts', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Error al cargar cuentas: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setAccounts(data);
      } else {
        setAccounts([]);
      }
    } catch (err) {
      console.error('Error cargando cuentas:', err);
      setError(`Error al cargar cuentas: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  };

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      let parsedMethods = ALL_PAYMENT_METHODS.map(m => m.value);
      try {
        if (product.allowedPaymentMethods) {
          parsedMethods = JSON.parse(product.allowedPaymentMethods);
        }
      } catch (e) {
        console.error('Error parsing payment methods:', e);
      }
      setFormData({
        sku: product.sku,
        name: product.name,
        description: product.description || '',
        price: product.price,
        taxRate: product.taxRate,
        type: product.type,
        incomeAccountId: product.incomeAccountId.toString(),
        assetAccountId: product.assetAccountId?.toString() || '',
        allowedPaymentMethods: parsedMethods,
        isActive: product.isActive,
        defaultDiscountId: product.defaultDiscountId?.toString() || '',
        claveProdServ: product.claveProdServ || '',
        claveUnidad: product.claveUnidad || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        sku: '',
        name: '',
        description: '',
        price: '',
        taxRate: '0',
        type: 'PRODUCT',
        incomeAccountId: '',
        assetAccountId: '',
        allowedPaymentMethods: ALL_PAYMENT_METHODS.map(m => m.value),
        isActive: true,
        defaultDiscountId: '',
        claveProdServ: '',
        claveUnidad: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!formData.name || !formData.price || !formData.incomeAccountId) {
        setError('Por favor completa todos los campos requeridos');
        setSaving(false);
        return;
      }

      const incomeAccountIdNum = parseInt(formData.incomeAccountId);
      if (isNaN(incomeAccountIdNum)) {
        setError('Selecciona una cuenta de ingreso válida');
        setSaving(false);
        return;
      }

      const payload = {
        sku: formData.sku,
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        taxRate: parseFloat(formData.taxRate),
        type: formData.type,
        incomeAccountId: incomeAccountIdNum,
        assetAccountId: formData.assetAccountId ? parseInt(formData.assetAccountId) : null,
        allowedPaymentMethods: JSON.stringify(formData.allowedPaymentMethods),
        isActive: formData.isActive,
        defaultDiscountId: formData.defaultDiscountId ? parseInt(formData.defaultDiscountId) : null,
        claveProdServ: formData.claveProdServ || null,
        claveUnidad: formData.claveUnidad || null,
      };

      const url = editingProduct 
        ? `/api/sales/products/${editingProduct.id}`
        : '/api/sales/products';

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al guardar producto');
      }

      await loadProducts();
      handleCloseModal();
    } catch (err: any) {
      console.error('Error guardando producto:', err);
      setError(err.message || 'Error al guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      return;
    }

    try {
      setError('');
      const response = await fetch(`/api/sales/products/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMsg = 'Error al eliminar producto';
        if (contentType?.includes('application/json')) {
          const data = await response.json();
          errorMsg = data.error || errorMsg;
        }
        throw new Error(errorMsg);
      }

      await loadProducts();
    } catch (err: any) {
      console.error('Error eliminando producto:', err);
      setError(err.message || 'Error al eliminar el producto');
    }
  };

  const filteredProducts = products.filter(p =>
    p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.claveProdServ || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.claveUnidad || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const incomeAccounts = accounts.filter(a => a.type === 'INCOME');
  const assetAccounts = accounts.filter(a => a.type === 'ASSET');

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm p-6 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/catalogos')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Productos y Servicios</h1>
              <p className="text-gray-600 text-sm mt-1">Gestión del catálogo de productos y servicios</p>
            </div>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="h-5 w-5" />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Buscar por SKU, nombre, descripción o clave SAT..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-4">Cargando productos...</p>
          </div>
        )}

        {/* Products Table */}
        {!loading && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {filteredProducts.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hay productos aún. ¡Crea uno nuevo!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">SKU</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Nombre</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Tipo</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Precio c/IVA</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Tasa Fiscal</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Clave Prod/Serv</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Unidad SAT</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Cuenta Ingreso</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Descuento</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Estado</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="border-b hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{product.sku}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.description && (
                              <p className="text-gray-600 text-xs mt-1">{product.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            product.type === 'PRODUCT' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {product.type === 'PRODUCT' ? 'Producto' : 'Servicio'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <span className="font-medium">
                              ${(parseFloat(product.price) * (1 + parseFloat(product.taxRate) / 100)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            {parseFloat(product.taxRate) > 0 && (
                              <span className="block text-xs text-gray-400">base ${parseFloat(product.price).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{parseFloat(product.taxRate).toFixed(1)}%</td>
                        <td className="px-6 py-4 text-sm">
                          {product.claveProdServ ? (
                            <span className="font-mono text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">
                              {product.claveProdServ}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {product.claveUnidad ? (
                            <span className="font-mono text-xs bg-violet-50 text-violet-800 border border-violet-200 px-2 py-0.5 rounded">
                              {product.claveUnidad}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{product.incomeAccount?.code || '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          {product.defaultDiscount ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <Tag className="w-3 h-3" />
                              {product.defaultDiscount.type === 'PERCENTAGE'
                                ? `${parseFloat(product.defaultDiscount.value).toFixed(0)}%`
                                : `$${parseFloat(product.defaultDiscount.value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            product.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {product.isActive ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleOpenModal(product)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Editar"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-600 hover:text-gray-900"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU <span className="text-gray-400 font-normal">(opcional — se asigna automáticamente)</span>
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 3);
                    setFormData({ ...formData, sku: onlyDigits });
                  }}
                  onBlur={() => {
                    if (formData.sku) {
                      setFormData({ ...formData, sku: formData.sku.padStart(3, '0') });
                    }
                  }}
                  placeholder="Dejar vacío para asignar automáticamente"
                  maxLength={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del producto"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del producto"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tasa Fiscal (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.taxRate}
                    onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="PRODUCT">Producto</option>
                  <option value="SERVICE">Servicio</option>
                </select>
              </div>

              {/* SAT Catalogs section */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-amber-900">Claves SAT para CFDI</span>
                  <span className="text-xs text-amber-700 font-normal">(requeridas para facturación electrónica)</span>
                </div>
                <SatCombobox
                  label="Clave Producto/Servicio SAT"
                  value={formData.claveProdServ}
                  onChange={(val) => setFormData({ ...formData, claveProdServ: val })}
                  items={satProdServ}
                  loading={satLoading}
                  placeholder="Buscar clave prod/serv..."
                  getLabel={(item) => `${item.clave} — ${item.descripcion}`}
                />
                <SatCombobox
                  label="Unidad de Medida SAT"
                  value={formData.claveUnidad}
                  onChange={(val) => setFormData({ ...formData, claveUnidad: val })}
                  items={satUnidades}
                  loading={satLoading}
                  placeholder="Buscar unidad de medida..."
                  getLabel={(item) => `${item.clave} — ${item.nombre}`}
                />
                {satProdServ.length === 0 && !satLoading && (
                  <p className="text-xs text-amber-700">
                    El catálogo SAT aún no tiene datos. Ve a <strong>Admin → Facturación → Catálogos SAT</strong> y carga las claves.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta de Ingreso *</label>
                <select
                  value={formData.incomeAccountId}
                  onChange={(e) => setFormData({ ...formData, incomeAccountId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar cuenta...</option>
                  {incomeAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta de Activo (Opcional)</label>
                <select
                  value={formData.assetAccountId}
                  onChange={(e) => setFormData({ ...formData, assetAccountId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sin cuenta de activo</option>
                  {assetAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descuento predeterminado
                  <span className="text-gray-400 font-normal ml-1">(se aplica automáticamente en nuevas facturas)</span>
                </label>
                <select
                  value={formData.defaultDiscountId}
                  onChange={(e) => setFormData({ ...formData, defaultDiscountId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sin descuento predeterminado</option>
                  {discounts.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name}{d.code ? ` (${d.code})` : ''} — {d.type === 'PERCENTAGE' ? `${parseFloat(d.value).toFixed(0)}%` : `$${parseFloat(d.value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}
                    </option>
                  ))}
                </select>
                {discounts.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No hay descuentos activos. Crea uno en Catálogo de Descuentos.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Métodos de Pago Permitidos</label>
                <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                  {ALL_PAYMENT_METHODS.map((method) => (
                    <label key={method.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowedPaymentMethods.includes(method.value)}
                        onChange={(e) => {
                          const newMethods = e.target.checked
                            ? [...formData.allowedPaymentMethods, method.value]
                            : formData.allowedPaymentMethods.filter(m => m !== method.value);
                          setFormData({ ...formData, allowedPaymentMethods: newMethods });
                        }}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{method.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Selecciona los métodos de pago aceptados para este producto</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado del Producto</label>
                  <p className="text-xs text-gray-500">Los productos inactivos no aparecen en ventas</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.isActive ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
